import { defineMessages, FormattedMessage } from '@faceup/localization'
import { TextSelect, type TextSelection } from '@faceup/ui'
import { Button, Popover, Typography } from '@faceup/ui-base'
import { type ReactNode, useState } from 'react'
import { redactCharacter } from '../../utils'
import { useRedaction } from './RedactionProvider'
import type { RedactTextType } from './redactionConstants'
import { getTextByTextType } from './redactText'

const messages = defineMessages('report.components.redaction.textToRedact', [
  { id: 'anonymize', defaultMessage: 'Redact' },
  { id: 'deanonymize', defaultMessage: 'Unredact' },
] as const)

const getSplitText = (text: string, selection: TextSelection) => ({
  textBefore: text.substring(0, selection.start),
  textSelected: text.substring(selection.start, selection.end),
  textAfter: text.substring(selection.end),
})

type TextToRedactProps = {
  children: ReactNode
  type: RedactTextType
  text: string
  className?: string
}

export const TextToRedact = (props: TextToRedactProps) => {
  const { children, type, text: originalText, className } = props
  const [selectedText, setSelectedText] = useState<TextSelection>()
  const { isRedactionActive, texts, redactText } = useRedaction()
  const text = getTextByTextType(texts, type, originalText)

  if (!isRedactionActive) {
    return <div className={className}>{children}</div>
  }

  if (selectedText) {
    const { textBefore, textSelected, textAfter } = getSplitText(text, selectedText)
    return (
      <div onClick={() => setSelectedText(undefined)} className={className}>
        {textBefore}
        <Popover
          content={
            <div>
              <Button
                ghost
                type='primary'
                onClick={() =>
                  redactText(
                    type,
                    {
                      start: selectedText.start,
                      end: selectedText.end,
                      variant: 'redacted',
                    },
                    originalText
                  )
                }
              >
                <FormattedMessage message={messages.anonymize} />
              </Button>
              <Button
                ghost
                type='primary'
                onClick={() =>
                  redactText(
                    type,
                    {
                      start: selectedText.start,
                      end: selectedText.end,
                      variant: 'notRedacted',
                    },
                    originalText
                  )
                }
              >
                <FormattedMessage message={messages.deanonymize} />
              </Button>
            </div>
          }
        >
          <Typography.Text mark className='p-0'>
            {textSelected}
          </Typography.Text>
        </Popover>
        {textAfter}
      </div>
    )
  }

  return (
    <TextSelect
      text={text}
      onTextSelect={(selectedText: TextSelection) => {
        const { textSelected } = getSplitText(text, selectedText)
        const isSomethingInSelectionRedacted = textSelected.includes(redactCharacter)
        if (isSomethingInSelectionRedacted) {
          setSelectedText(selectedText)
        } else {
          redactText(
            type,
            {
              start: selectedText.start,
              end: selectedText.end,
              variant: 'redacted',
            },
            originalText
          )
        }
      }}
    />
  )
}
